import { useEffect, useState, useCallback, useRef } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Import logger if available, otherwise create a simple logger
const logger = {
  info: (...args: unknown[]) => console.log(...args),
  error: (...args: unknown[]) => console.error(...args),
};

export interface AddressComponents {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

export interface GoogleAddress {
  formattedAddress: string;
  addressComponents: AddressComponents;
  location: {
    lat: number;
    lng: number;
  };
  raw: google.maps.places.PlaceResult;
}

/**
 * Optimized props interface for the AutocompleteAddress component
 */
export interface AutocompleteAddressProps {
  /** Callback when an address is selected or edited */
  onAddressChange: (address: GoogleAddress | null) => void;

  /** Callback specifically for line2 changes */
  onLine2Change?: (value: string) => void;

  /** Display mode for the component */
  mode?: "search" | "view" | "edit";

  /** Initial address data */
  initialAddress?: {
    /** Formatted address string to display */
    formattedString?: string;
    /** Individual address components */
    components?: AddressComponents;
    /** Geolocation coordinates */
    location?: { lat: number; lng: number };
  };

  /** Control which fields can be edited (when in edit mode) */
  editableFields?: {
    line1?: boolean;
    line2?: boolean;
    city?: boolean;
    state?: boolean;
    postalCode?: boolean;
    country?: boolean;
  };

  /** Placeholder text for the search input */
  placeholder?: string;

  /** Additional UI configuration */
  ui?: {
    /** Always show the full form instead of collapsible */
    alwaysShowForm?: boolean;
    /** Hide the edit/reset buttons */
    hideControls?: boolean;
    /** Only show the autocomplete search field */
    onlyShowSearch?: boolean;
  };
}

// Legacy props interface for backward compatibility
interface LegacyProps {
  onSelectedAddress: (address: GoogleAddress | null) => void;
  onLine2Change?: (value: string) => void;
  placeholder?: string;
  initialValue?: string;
  initialAddressComponents?: AddressComponents;
  showEditFormByDefault?: boolean;
  readOnly?: boolean;
  readOnlyComponents?: boolean;
  showFullForm?: boolean;
  onlyShowAutocomplete?: boolean;
}

// Combined props type that accepts either the new or legacy props
type Props = AutocompleteAddressProps | LegacyProps;

// Type guard to check if props are using the new interface
function isNewProps(props: Props): props is AutocompleteAddressProps {
  return "onAddressChange" in props;
}

export function AutocompleteAddress(props: Props) {
  // Convert legacy props to new props format if needed
  const normalizedProps: AutocompleteAddressProps = isNewProps(props)
    ? props
    : {
        onAddressChange: props.onSelectedAddress,
        mode: props.readOnly
          ? "view"
          : props.showEditFormByDefault
            ? "edit"
            : "search",
        initialAddress: props.initialAddressComponents
          ? {
              formattedString: props.initialValue,
              components: props.initialAddressComponents,
              location: { lat: 0, lng: 0 },
            }
          : undefined,
        editableFields: props.readOnlyComponents
          ? {}
          : {
              line1: true,
              line2: true,
              city: true,
              state: true,
              postalCode: true,
              country: true,
            },
        placeholder: props.placeholder,
        ui: {
          alwaysShowForm: props.showFullForm,
          hideControls: props.readOnly,
          onlyShowSearch: props.onlyShowAutocomplete,
        },
        onLine2Change: props.onLine2Change,
      };

  const {
    onAddressChange,
    mode = "search",
    initialAddress,
    editableFields = {
      line1: true,
      line2: true,
      city: true,
      state: true,
      postalCode: true,
      country: true,
    },
    placeholder,
    ui = {},
    onLine2Change,
  } = normalizedProps;

  // Helper function to format address - define it before state initialization
  const getFormattedAddress = useCallback(
    (components: AddressComponents): string => {
      const line1 = components.line1;
      // Preserve line2 exactly as it is, without any trimming or modification
      const line2 = components.line2;

      console.log("getFormattedAddress - line2 value:", JSON.stringify(line2));

      // For city and province, combine them properly
      let cityProvince = "";
      if (components.city && components.state) {
        cityProvince = `${components.city}, ${components.state}`;
      } else if (components.city) {
        cityProvince = components.city;
      } else if (components.state) {
        cityProvince = components.state;
      }

      // Add postal code if available
      const cityProvPostal = components.postalCode
        ? `${cityProvince} ${components.postalCode}`
        : cityProvince;

      // Include country in the formatted address
      const country = components.country ?? "Canada";

      // Build the parts array, including line2 if it exists
      const parts = [line1, line2 || null, cityProvPostal, country].filter(
        Boolean
      );

      const formattedAddress = parts.join(", ");
      console.log("Formatted address:", formattedAddress);

      return formattedAddress;
    },
    []
  );

  const map = useMap();
  const places = useMapsLibrary("places");

  const [isOpen, setIsOpen] = useState(false);
  const [showEditForm, setShowEditForm] = useState(
    ui.alwaysShowForm ?? mode === "edit"
  );
  const [inputValue, setInputValue] = useState(
    initialAddress?.formattedString ?? ""
  );
  const [selectedPlace, setSelectedPlace] = useState<GoogleAddress | null>(
    initialAddress?.components
      ? {
          formattedAddress:
            initialAddress.formattedString ??
            getFormattedAddress(initialAddress.components),
          addressComponents: initialAddress.components,
          location: initialAddress.location ?? { lat: 0, lng: 0 },
          raw: {} as google.maps.places.PlaceResult,
        }
      : null
  );
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  // Services
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);

  // Form state
  const [editableAddress, setEditableAddress] = useState<AddressComponents>(
    initialAddress?.components ?? {
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    }
  );

  // Add a ref to track if we're interacting with the edit form
  const editFormRef = useRef<HTMLDivElement>(null);

  // Add state to track if we're editing
  const [isEditing, setIsEditing] = useState(
    ui.alwaysShowForm ?? mode === "edit"
  );

  // Update form when order data is loaded
  useEffect(() => {
    if (initialAddress?.components) {
      console.log("AutocompleteAddress initializing with:", initialAddress);

      // Ensure the formatted address is properly generated if not provided
      const formattedAddr =
        initialAddress.formattedString ??
        getFormattedAddress(initialAddress.components);

      // Preserve line2 exactly as it is
      const addressComponents = {
        ...initialAddress.components,
        line2: initialAddress.components.line2 || "",
      };

      // Set the selected place with the initial address data
      setSelectedPlace({
        formattedAddress: formattedAddr,
        addressComponents: addressComponents,
        location: initialAddress.location ?? { lat: 0, lng: 0 },
        raw: {} as google.maps.places.PlaceResult,
      });

      // Also update the editable address state
      setEditableAddress(addressComponents);
    }
  }, [initialAddress]);

  // Initialize Google services
  useEffect(() => {
    if (!places || !map) return;

    try {
      const autocomplete = new places.AutocompleteService();
      const placesServ = new places.PlacesService(map);
      const token = new places.AutocompleteSessionToken();

      setAutocompleteService(autocomplete);
      setPlacesService(placesServ);
      setSessionToken(token);
    } catch (error) {
      logger.error("Error initializing services:", error);
    }
  }, [map, places]);

  // Handle predictions fetch
  const fetchPredictions = useCallback(
    async (input: string) => {
      logger.info("Fetching predictions for:", input);
      if (!autocompleteService || !input) {
        setPredictions([]);
        return;
      }

      try {
        const response = await autocompleteService.getPlacePredictions({
          input,
          sessionToken,
          componentRestrictions: { country: "ca" },
          region: "CA",
          language: "en",
        });

        // Add debug logging
        logger.info("Predictions response:", response);

        setPredictions(response?.predictions || []);
      } catch (error) {
        logger.error("Error fetching predictions:", error);
        setPredictions([]);
      }
    },
    [autocompleteService, sessionToken]
  );

  // Handle place selection
  const handlePlaceSelect = useCallback(
    async (placeId: string) => {
      if (!placesService || !places) return;

      try {
        await new Promise((resolve, reject) => {
          placesService.getDetails(
            {
              placeId,
              fields: ["address_components", "geometry", "formatted_address"],
              sessionToken,
              language: "en",
              region: "CA",
            },
            (result, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                result
              ) {
                // Check for subpremise (unit/apartment number) in address components
                const subpremise = result.address_components?.find(
                  (component) => component.types.includes("subpremise")
                )?.long_name;

                const address: GoogleAddress = {
                  formattedAddress: result.formatted_address ?? "",
                  addressComponents: {
                    line1: "",
                    line2: subpremise ?? "",
                    city: "",
                    state: "",
                    country: "",
                    postalCode: "",
                  },
                  location: {
                    lat: result.geometry?.location?.lat() ?? 0,
                    lng: result.geometry?.location?.lng() ?? 0,
                  },
                  raw: result,
                };

                // Parse address components
                const streetNumber =
                  result.address_components?.find((component) =>
                    component.types.includes("street_number")
                  )?.long_name ?? "";

                const route =
                  result.address_components?.find((component) =>
                    component.types.includes("route")
                  )?.long_name ?? "";

                // Combine street number and route for line1
                address.addressComponents.line1 =
                  `${streetNumber} ${route}`.trim();

                // Parse other components
                result.address_components?.forEach((component) => {
                  const types = component.types;
                  if (types.includes("locality")) {
                    address.addressComponents.city = component.long_name;
                  }
                  if (types.includes("administrative_area_level_1")) {
                    address.addressComponents.state = component.long_name;
                  }
                  if (types.includes("country")) {
                    address.addressComponents.country =
                      component.short_name === "CA"
                        ? "Canada"
                        : component.short_name;
                  }
                  if (types.includes("postal_code")) {
                    address.addressComponents.postalCode = component.long_name;
                  }
                });

                setSelectedPlace(address);
                setEditableAddress(address.addressComponents);
                setShowEditForm(true);
                setIsEditing(true);
                onAddressChange(address);
                setSessionToken(new places.AutocompleteSessionToken());
                resolve(address);
              } else {
                reject(new Error(`Place details failed: ${status}`));
              }
            }
          );
        });
      } catch (error) {
        logger.error("Error fetching place details:", error);
      }
    },
    [onAddressChange, places, placesService, sessionToken]
  );

  const handleSaveEdit = useCallback(() => {
    if (selectedPlace) {
      console.log("Saving edit - current editable address:", editableAddress);

      // Ensure line2 is preserved exactly as is
      const updatedComponents = {
        ...editableAddress,
        line2: editableAddress.line2 || "",
      };

      const updatedAddress = {
        ...selectedPlace,
        addressComponents: updatedComponents,
        formattedAddress: getFormattedAddress(updatedComponents),
      };

      console.log("Saving edit - updated address:", updatedAddress);

      setSelectedPlace(updatedAddress);
      onAddressChange(updatedAddress);
      setShowEditForm(false);
      setIsEditing(false);
    }
  }, [selectedPlace, editableAddress, onAddressChange, getFormattedAddress]);

  const handleReset = useCallback(() => {
    setSelectedPlace(null);
    setShowEditForm(false);
    setInputValue("");
    setEditableAddress({
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    });
    setIsEditing(false);
    onAddressChange(null);
  }, [onAddressChange]);

  // Handle line2 changes separately to maintain consistency
  const handleLine2Change = useCallback(
    (value: string) => {
      console.log("Line2 changed to:", value);
      setEditableAddress((prev) => ({
        ...prev,
        line2: value,
      }));

      // Call the onLine2Change prop if provided
      if (onLine2Change) {
        onLine2Change(value);
      }
    },
    [onLine2Change]
  );

  // Determine if the component is in read-only mode
  const isReadOnly = mode === "view";

  // Determine if the form fields should be read-only
  const areFieldsReadOnly = (field: keyof AddressComponents) =>
    !editableFields[field];

  // Determine if the edit form should be shown
  const shouldShowEditForm =
    ui.alwaysShowForm === true ? true : showEditForm && !isReadOnly;

  // Determine if the controls should be hidden
  const shouldHideControls = ui.hideControls;

  // Determine if only the search should be shown
  const onlyShowSearch = ui.onlyShowSearch;

  return (
    <div className="space-y-4">
      {selectedPlace ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input value={getFormattedAddress(editableAddress)} readOnly />
            {!isReadOnly && !shouldHideControls && (
              <>
                {!onlyShowSearch && !ui.alwaysShowForm && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      // Don't toggle if alwaysShowForm is true
                      if (!ui.alwaysShowForm) {
                        setShowEditForm(!showEditForm);
                        setIsEditing(!isEditing);
                      }
                    }}
                  >
                    {showEditForm ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    handleReset();
                    setIsEditing(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {shouldShowEditForm && !onlyShowSearch && (
            <div
              ref={editFormRef}
              className="space-y-4 border rounded-lg p-4 bg-background"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Address Line 1</Label>
                    <Input
                      value={editableAddress.line1}
                      onChange={(e) =>
                        setEditableAddress({
                          ...editableAddress,
                          line1: e.target.value,
                        })
                      }
                      readOnly={areFieldsReadOnly("line1")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 2 (Unit/Suite)</Label>
                    <Input
                      value={editableAddress.line2}
                      onChange={(e) => handleLine2Change(e.target.value)}
                      placeholder="Apartment, suite, unit, etc."
                      readOnly={areFieldsReadOnly("line2")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={editableAddress.city}
                      onChange={(e) =>
                        setEditableAddress({
                          ...editableAddress,
                          city: e.target.value,
                        })
                      }
                      readOnly={areFieldsReadOnly("city")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State/Province</Label>
                    <Input
                      value={editableAddress.state}
                      onChange={(e) =>
                        setEditableAddress({
                          ...editableAddress,
                          state: e.target.value,
                        })
                      }
                      readOnly={areFieldsReadOnly("state")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Postal Code</Label>
                    <Input
                      value={editableAddress.postalCode}
                      onChange={(e) =>
                        setEditableAddress({
                          ...editableAddress,
                          postalCode: e.target.value,
                        })
                      }
                      readOnly={areFieldsReadOnly("postalCode")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={editableAddress.country}
                      onChange={(e) =>
                        setEditableAddress({
                          ...editableAddress,
                          country: e.target.value,
                        })
                      }
                      readOnly={areFieldsReadOnly("country")}
                    />
                  </div>
                </div>
              </div>

              {Object.values(editableFields).some(Boolean) &&
                !ui.alwaysShowForm && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false);
                        setIsEditing(false);
                      }}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} type="button">
                      Save
                    </Button>
                  </div>
                )}
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              void fetchPredictions(e.target.value);
            }}
            onBlur={(e) => {
              if (!editFormRef.current?.contains(e.relatedTarget as Node)) {
                setTimeout(() => {
                  setPredictions([]);
                  setIsOpen(false);
                }, 200);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder ?? "Search for an address"}
            className="w-full"
            disabled={isReadOnly}
          />

          {isOpen && predictions.length > 0 && !isReadOnly && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md">
              <ul className="max-h-[300px] overflow-auto py-1">
                {predictions.map((prediction) => (
                  <li
                    key={prediction.place_id}
                    onClick={() => {
                      void handlePlaceSelect(prediction.place_id);
                      setIsOpen(false);
                      setInputValue("");
                    }}
                    className={cn(
                      "px-3 py-2 text-sm cursor-pointer",
                      "hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    {prediction.description}
                  </li>
                ))}
                {predictions.length === 0 && (
                  <li className="px-3 py-2 text-sm text-muted-foreground text-center">
                    {inputValue === ""
                      ? "Start typing to search..."
                      : "No results found"}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
